import { Link } from "react-router-dom";
import {
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@volca/ui/components/ui/sidebar";
import { cn } from "@volca/ui/lib/utils";
import type { SidebarSection } from "./nav-items";
import { SectionStatus } from "./section-status";
import { SIDEBAR_ICON_CURRENT } from "./sidebar-row";
import { rowShowsActive } from "./use-row-expanded";

// A tapped row's drawer closes as the page changes, so a fading highlight
// only lingers on the row just left: drawer rows switch at once.
const INSTANT =
  "transition-none before:transition-none [&_svg]:transition-none";

const RowContent = ({
  row,
  active,
}: {
  row: SidebarSection;
  active: boolean;
}) => (
  <>
    {row.icon && (
      <row.icon
        weight={active ? "duotone" : "regular"}
        className={cn({ [SIDEBAR_ICON_CURRENT]: active })}
      />
    )}
    <span>{row.label}</span>
    <SectionStatus section={row} />
  </>
);

/**
 * A drawer row. One that holds others (an agent) shows them under it while
 * it is the one on screen, with its action (Settings) at the row's end.
 */
export const MobileSectionRow = ({
  row,
  onNavigate,
}: {
  row: SidebarSection;
  onNavigate: () => void;
}) => {
  if (row.pending)
    return (
      <SidebarMenuItem aria-hidden>
        <SidebarMenuSkeleton showIcon />
      </SidebarMenuItem>
    );
  const children = row.expanded ? (row.children ?? []) : [];
  const active = rowShowsActive(row, children.length > 0);
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={active}
        className={INSTANT}
        render={
          <Link
            to={row.to}
            onClick={onNavigate}
            aria-current={row.isActive ? "page" : undefined}
          />
        }
      >
        <RowContent row={row} active={active} />
      </SidebarMenuButton>
      {row.action && (
        <SidebarMenuAction
          render={
            <Link
              to={row.action.to}
              onClick={onNavigate}
              aria-label={`${row.label} ${row.action.label}`}
              aria-current={row.action.isActive ? "page" : undefined}
            />
          }
          className={cn({ "text-accent-orange": row.action.isActive })}
        >
          <row.action.icon
            weight={row.action.isActive ? "duotone" : "regular"}
          />
        </SidebarMenuAction>
      )}
      {children.length > 0 && (
        <SidebarMenuSub>
          {children.map((child) =>
            child.pending ? (
              <SidebarMenuSubItem key={child.to} aria-hidden>
                <SidebarMenuSkeleton />
              </SidebarMenuSubItem>
            ) : (
              <SidebarMenuSubItem key={child.to}>
                <SidebarMenuSubButton
                  isActive={child.isActive}
                  render={
                    <Link
                      to={child.to}
                      onClick={onNavigate}
                      aria-current={child.isActive ? "page" : undefined}
                    />
                  }
                >
                  <RowContent row={child} active={child.isActive} />
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ),
          )}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
};
